import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { Container, PageHeader, Section, SettingRow } from '@vnetwork/ui/layout';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { useServiceCdnOverview } from '@/api/manticore/service-cdn';
import { useMyProjects } from '@/api/projects';
import { CdnConsoleBreadcrumbs } from './CdnConsoleBreadcrumbs';
import { ProviderStrip } from '../ProviderStrip';
import { UmbrellaAssignProjectDialog } from './UmbrellaAssignProjectDialog';
import { CdnRenameServiceDialog } from './CdnRenameServiceDialog';
import { CdnDeleteServiceDialog } from './CdnDeleteServiceDialog';
import styles from './CdnSettings.module.css';

/**
 * Settings page for a scoped CDN orchestrator console.
 * Exposes the orchestrator name (rename), provider family and project assignment.
 */
export function CdnSettingsPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [assignOpen, setAssignOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: umbrData, isLoading: umbrLoading } = useCdnUmbrellas();
  const { data: projects = [], isLoading: projLoading } = useMyProjects();
  // Domain count gates the delete: only an empty service may be removed
  // (the BE enforces the same rule with a 409).
  const { data: svcOverview } = useServiceCdnOverview(serviceId ?? '');
  const domainCount = svcOverview?.total ?? 0;

  const umbrella = useMemo(
    () => (umbrData?.umbrellas ?? []).find((u) => u.service_id === serviceId),
    [umbrData, serviceId],
  );

  const projectName = useMemo(() => {
    if (!umbrella?.project_id) return undefined;
    return projects.find((p) => p.id === umbrella.project_id)?.name;
  }, [umbrella, projects]);

  const isLoading = umbrLoading || projLoading;
  const serviceName = umbrella?.service_name ?? '';

  const nameControl = isLoading ? (
    <Skeleton style={{ height: 28, width: 160 }} />
  ) : (
    <div className={styles.controlRow}>
      <Badge tone="neutral" appearance="subtle">{serviceName || '—'}</Badge>
      <Button variant="secondary" size="sm" disabled={!umbrella} onClick={() => setRenameOpen(true)}>
        Rename
      </Button>
    </div>
  );

  const projectControl = isLoading ? (
    <Skeleton style={{ height: 28, width: 120 }} />
  ) : umbrella?.project_id && projectName ? (
    <div className={styles.controlRow}>
      <Badge tone="neutral" appearance="subtle">{projectName}</Badge>
      <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
        Change
      </Button>
    </div>
  ) : (
    <div className={styles.controlRow}>
      <span className={styles.noProject}>No project</span>
      <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
        Change
      </Button>
    </div>
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Settings size={22} />}
        breadcrumbs={<CdnConsoleBreadcrumbs trail={[{ label: 'Settings' }]} />}
        title={umbrella?.service_name ?? serviceId ?? 'Settings'}
        description="Manage this orchestrator."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section
        title="General"
        description="Basic details for this CDN orchestrator."
        style={{ marginBottom: 0 }}
      >
        <SettingRow
          title="Service name"
          description="The display name shown across the console and CDN home page."
          control={nameControl}
        />
      </Section>

      {serviceId && <ProviderStrip serviceId={serviceId} />}

      <Section
        title="Project assignment"
        description="Group this orchestrator under a project on the CDN home page."
        style={{ marginBottom: 0 }}
      >
        <SettingRow
          title="Project"
          description="Assign this orchestrator to a project to organise it on the CDN home page."
          control={projectControl}
        />
      </Section>

      <Section
        title="Danger zone"
        description="Destructive actions for this CDN orchestrator."
        style={{ marginBottom: 0 }}
      >
        <SettingRow
          title="Delete this service"
          description={
            domainCount > 0
              ? `Blocked: ${domainCount} domain${domainCount === 1 ? '' : 's'} still bound — move or delete them first.`
              : 'Removes the orchestrator and all its provider backends. Recorded in the activity log.'
          }
          control={
            <Button
              variant="danger"
              size="sm"
              disabled={!umbrella || domainCount > 0}
              onClick={() => setDeleteOpen(true)}
            >
              Delete service
            </Button>
          }
        />
      </Section>
      </div>

      {umbrella && (
        <>
          <CdnDeleteServiceDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            serviceId={umbrella.service_id}
            serviceName={umbrella.service_name}
            domainCount={domainCount}
          />
          <CdnRenameServiceDialog
            open={renameOpen}
            onOpenChange={setRenameOpen}
            serviceId={umbrella.service_id}
            currentName={umbrella.service_name}
          />
          <UmbrellaAssignProjectDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            serviceId={umbrella.service_id}
            serviceName={umbrella.service_name}
            currentProjectId={umbrella.project_id}
          />
        </>
      )}
    </Container>
  );
}
